import React, { useEffect, useMemo, useState } from "react";
import { Typography, Select, Button, Card, Divider, Pagination } from "antd";
import { FilterOutlined, DownOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import LeadCard from "./LeadCard";
import PriorityLeadCard from "./PriorityLeadCard";
import StatisticsChart from "./StatisticsChart";
import "./newDashboard.css";
import useLeadOptions from "../../components/NewDashboard/UseLeadOptions";
import useBusinessDashboardApi from "../../hooks/useBusinessDashboardApi";
import { formatAmountUAE } from "./FormatAmount";

const { Title, Text } = Typography;

export default function MyLeadsBusinessRetailMobile() {
  const navigate = useNavigate();
  const location = useLocation();

  /* ---------------- STATE ---------------- */
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState("qualified");
  // const [localStatusMap, setLocalStatusMap] = useState({});

  const [filters, setFilters] = useState({
    location: [],
    landType: [],
    budget: [],
    plotSize: [],
  });

  /* ---------------- API HOOK ---------------- */
  const {
    allLeads,
    pagination,
    cartLeads,
    myRetailLeads,
    retailPagination,
    fetchMyRetailLeads,
    fetchAllLeads,
    updateLeadStatus,
    addLeadToCart,
    removeLeadFromCart,
  } = useBusinessDashboardApi();

  /* ---------------- NAV FILTER SUPPORT ---------------- */
  useEffect(() => {
    if (location.state?.filters) {
      const f = location.state.filters;
      setFilters({
        location: f.location || [],
        landType: f.landType || [],
        budget: f.budget || [],
        plotSize: f.plotSize || [],
      });
    }
  }, [location.state]);

  /* ---------------- NORMALIZE LEADS ---------------- */
  const leads = useMemo(() => {
    return (Array.isArray(myRetailLeads) ? myRetailLeads : []).map((lead) => ({
      id: lead._id,
      name: lead.name,
      place: lead.location,
      price: lead.budget,
      currentRate: lead.currentRate,
      landType: lead.subcategory?.label || "",
      rating: `${lead.timesBought || 0}/${lead.leadAvailableCount || 0}`,
      user_status: lead.user_status || "",
      // user_status: localStatusMap[lead._id] ?? lead.user_status ?? "",
      lookingFor: lead.property_details || "",
      contact: lead.phone,
      type: lead.leadType,
      sqft: lead.spaceSqft || 0,
      state: lead.state || "",
      district: lead.district || "",
      area: lead.area || "",
      pincode: lead.pincode || "",
      siteVisit: lead.siteVisitDays?.join(", ") || "",
      purpose: lead.purpose || "",
    }));
  }, [myRetailLeads]);

  /* ---------------- OPTIONS ---------------- */
  const { locationOptions, landTypeOptions, budgetOptions, plotSizeOptions } =
    useLeadOptions(leads);

  const statusOptions = [
    { label: "Qualified", value: "qualified" },
    { label: "RNR", value: "rnr" },
    { label: "Intake", value: "intake" },
    { label: "Disqualified", value: "disqualified" },
  ];

  /* ---------------- FILTERED LEADS ---------------- */
  const filteredLeads = leads.filter((lead) => {
    return (
      (!filters.location.length || filters.location.includes(lead.place)) &&
      (!filters.landType.length || filters.landType.includes(lead.landType)) &&
      (!filters.budget.length ||
        filters.budget.some((b) => Number(lead.price) <= Number(b))) &&
      (!filters.plotSize.length ||
        filters.plotSize.some((s) => Number(lead.sqft) >= Number(s)))
    );
  });

  /* ---------------- PRIORITY LEAD ---------------- */
  const priorityLead = filteredLeads.find((lead) => {
    const [purchased, total] = lead.rating.split("/").map(Number);
    return total - purchased === 1;
  });

  /* ---------------- STATUS GROUPING ---------------- */
  const leadsByStatus = useMemo(() => {
    return {
      qualified: leads.filter((l) => l.user_status === "qualified"),
      rnr: leads.filter((l) => l.user_status === "rnr"),
      intake: leads.filter((l) => l.user_status === "intake"),
      disqualified: leads.filter((l) => l.user_status === "disqualified"),
    };
  }, [leads]);

  const leadsToRender = filteredLeads;

  const finalMyLeads = React.useMemo(() => {
    return filteredLeads.filter((lead) => lead.purpose === "BUILD");
  }, [filteredLeads]);

  const bottomLeads = leadsByStatus[statusFilter] || [];

  return (
    <div style={{ padding: 0, paddingBottom: 90 }}>
      {/* ================= PRIORITY LEAD ================= */}
      {priorityLead && (
        <PriorityLeadCard
          name={priorityLead.name}
          place={priorityLead.place}
          rating={priorityLead.rating}
          onTopPickClick={(leadId) =>
            navigate(`/user-dashboard?selectedleadid=${leadId}`)
          }
        />
      )}

      {/* ================= ALL LEADS ================= */}
      <Title level={4} style={{ marginTop: 16 }}>
        My Retail Leads
      </Title>

      <div style={{ background: "#fff", padding: 6 }}>
        {filteredLeads.map((lead) => (
          <div key={lead.id}>
            <LeadCard
              {...lead}
              onClick={() =>
                setSelectedLead(selectedLead?.id === lead.id ? null : lead)
              }
            />

            {/* ===== INLINE DETAILS ===== */}
            {selectedLead?.id === lead.id && (
              <Card style={{ background: "#fafafa", marginTop: 8 }}>
                {/* <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                > */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <Text strong style={{ marginBottom: 4 }}>
                    Update Lead
                  </Text>
                  {/* STATUS UPDATE */}
                  <Select
                    className="custom-select"
                    placeholder="Update Status"
                    value={selectedLead.user_status}
                    options={statusOptions}
                    style={{
                      border: "1px solid #DBEAFF",
                      borderRadius: 6,
                      background: "#fff",
                      width: "55%",
                      minWidth: 140,
                    }}
                    onChange={(value) => {
                      updateLeadStatus(lead.id, value);
                      setSelectedLead((prev) => ({
                        ...prev,
                        user_status: value,
                      }));
                    }}
                  />
                </div>

                {/* </div> */}
                <Text strong>Lead Details</Text>
                <Divider style={{ margin: "6px 0" }} />

                {/* GRID ROW 1 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Name</strong>
                    <div>{lead.name}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Contact No.</strong>
                    <div>{lead.contact || "-"}</div>
                  </div>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Client Location</strong>
                    <div>{lead.place}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Sq.ft</strong>
                    <div>{lead.sqft}</div>
                  </div>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                {/* GRID ROW 3 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Budget</strong>
                    <div>
                      {formatAmountUAE(lead.price)}{" "}
                      <span className="text-xs text-gray-500 ml-1">AED</span>
                    </div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Preferred location</strong>
                    <p>
                      {lead?.state || ""}-{lead?.district || ""}-
                      {lead?.area || ""}--
                      {lead?.pincode || ""}
                    </p>
                  </div>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                {/* GRID ROW 4 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1px 1fr",
                    alignItems: "center",
                  }}
                >
                  <div style={{ padding: 6 }}>
                    <strong>Site Visit</strong>
                    <div>{lead.siteVisit}</div>
                  </div>

                  <div
                    style={{ width: 1, height: "100%", background: "#DBEAFF" }}
                  />

                  <div style={{ padding: 6 }}>
                    <strong>Looking For</strong>
                    <div>{lead.lookingFor}</div>
                  </div>
                </div>

                {/* <Button
                  type="primary"
                  block
                  style={{ marginTop: 12 }}
                  //   onClick={() => handleLeadCheck(lead, true)}
                >
                  Buy Now
                </Button> */}
              </Card>
            )}
          </div>
        ))}
      </div>

      <Pagination
        simple
        showSizeChanger={false}
        current={retailPagination.currentPage}
        total={retailPagination.total || 10}
        pageSize={retailPagination.perPage}
        onChange={(page) => fetchMyRetailLeads(page)}
        style={{ marginTop: 16, textAlign: "center" }}
      />

      {/* ================= CHART ================= */}
      <Title level={5} style={{ marginTop: 16 }}>
        Lead Statistics
      </Title>

      <Card>
        <StatisticsChart />
      </Card>

      {/* ================= STATUS DROPDOWN (BOTTOM) ================= */}
      <div style={{ marginTop: 16 }}>
        <Title level={5} style={{ marginTop: 16 }}>
          Lead Update
        </Title>
        <Select
          value={statusFilter}
          bordered={false}
          suffixIcon={<DownOutlined />}
          options={statusOptions}
          onChange={setStatusFilter}
          style={{
            border: "1px solid #DBEAFF",
            borderRadius: 6,
            background: "#fff",
            width: "100%",
          }}
        />
      </div>

      <Card style={{ marginTop: 8 }}>
        {bottomLeads.map((lead) => (
          <LeadCard
            key={lead.id}
            {...lead}
            showCheckbox={false}
            onClick={() => setSelectedLead(lead)}
          />
        ))}
      </Card>

      {/* ================= CART BAR ================= */}
      {/* {cartLeads.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "#fff",
            borderTop: "1px solid #ddd",
            padding: 12,
            display: "flex",
            justifyContent: "space-between",
            zIndex: 1000,
          }}
        >
          <div>
            <strong>{cartLeads.length}</strong>{" "}
            {cartLeads.length === 1 ? "lead" : "leads"} selected
            <div style={{ fontSize: 12, color: "#666" }}>
              * Amount may change in real time
            </div>
          </div>
          <Button type="primary" onClick={() => navigate("/my-cart")}>
            View Cart
          </Button>
        </div>
      )} */}
    </div>
  );
}
